import { forwardRef, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/shared/lib/cn'

const textAreaVariants = cva(
  'w-full rounded-lg bg-white/5 border border-white/10 text-[#EEEEEE] placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#5E6AD2]/40 focus:border-transparent transition-colors text-base md:text-sm disabled:opacity-50 disabled:cursor-not-allowed resize-none',
  {
    variants: {
      hasError: {
        true: 'border-red-500/40 focus:ring-red-500/40',
        false: '',
      },
    },
    defaultVariants: {
      hasError: false,
    },
  }
)

export interface FormTextAreaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textAreaVariants> {
  label?: ReactNode
  error?: string
  containerClassName?: string
}

export const FormTextArea = forwardRef<HTMLTextAreaElement, FormTextAreaProps>(
  ({ label, error, className, containerClassName, hasError, id, ...props }, ref) => {
    const showError = Boolean(error) || hasError

    return (
      <div className={cn('space-y-1.5', containerClassName)}>
        {label && (
          <label
            htmlFor={id}
            className="block text-xs font-medium text-[#888888] uppercase tracking-wider"
          >
            {label}
          </label>
        )}
        <textarea
          id={id}
          ref={ref}
          className={cn(textAreaVariants({ hasError: showError }), className)}
          {...props}
        />
        {error && <p className="text-xs text-red-400">{error}</p>}
      </div>
    )
  }
)

FormTextArea.displayName = 'FormTextArea'
